import { IMAGES } from "../lib/assets";
import { Reveal } from "../components/Reveal";
import { motion, useScroll, useTransform } from "framer-motion";
import { TreeDeciduous, Compass, Pyramid, Amphora, Globe } from "lucide-react";

const PRINCIPLES = [
  {
    icon: TreeDeciduous,
    title: "Stewardship",
    text: "We are long-term stewards of capital and legacy.",
  },
  {
    icon: Compass,
    title: "Discipline",
    text: "We invest with discipline, patience, and conviction.",
  },
  {
    icon: Pyramid,
    title: "Integrity",
    text: "We operate with integrity, transparency, and humility.",
  },
  {
    icon: Amphora,
    title: "Freedom",
    text: "We create the freedom to live with purpose and intention.",
  },
  {
    icon: Globe,
    title: "Legacy",
    text: "We build enduring value for future generations.",
  },
];

export default function Home() {
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 900], [0, 140]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative h-screen min-h-[640px] w-full overflow-hidden"
      >
        <motion.img
          src={IMAGES.heroOffice}
          alt="Modern coastal family office at dusk overlooking the Pacific Ocean"
          style={{ y: heroY }}
          className="absolute -top-[8%] left-0 h-[116%] w-full object-cover brightness-110 animate-fade-in"
          loading="eager"
        />
        {/* Lighter, warmer overlay — lets the architecture glow through; text stays legible on the left */}
        <div className="absolute inset-0 bg-gradient-to-r from-aveden-black/75 via-aveden-black/20 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-aveden-black/55 via-transparent to-aveden-black/15" />

        <div className="relative h-full mx-auto max-w-[1600px] px-6 sm:px-10 lg:px-16 flex items-center">
          <div className="max-w-3xl animate-fade-up">
            <h1
              data-testid="hero-heading"
              aria-label="Operating, Investing, Building"
              className="font-serif font-light text-aveden-white text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight uppercase"
            >
              <span className="block">Operating</span>
              <span className="block pl-[1.1em]">Investing</span>
              <span className="block pl-[2.2em]">Building</span>
            </h1>
            <div className="h-px w-16 bg-aveden-gold mt-10 mb-8" />
            <p className="font-sans font-light text-aveden-gray text-base sm:text-lg leading-relaxed max-w-md">
              Investing in businesses, real assets, and opportunities aligned
              with long-term value creation.
            </p>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY STATEMENT — beneath hero */}
      <section
        data-testid="statement-section"
        className="bg-aveden-black py-20 lg:py-28 border-b border-white/[0.05]"
      >
        <div className="mx-auto max-w-4xl px-6 sm:px-10 text-center">
          <Reveal>
            <p className="font-serif font-light text-aveden-white/90 text-2xl sm:text-3xl lg:text-[2rem] leading-[1.5] tracking-wide text-balance">
              We invest with discipline, think in generations, and steward
              capital with a long-term perspective.
            </p>
          </Reveal>
        </div>
      </section>

      {/* SECTION 2 — MISSION */}
      <section
        data-testid="mission-section"
        className="bg-aveden-black pt-28 lg:pt-36 pb-12 lg:pb-16"
      >
        <div className="mx-auto max-w-5xl px-6 sm:px-10 text-center">
          <Reveal>
            <p className="font-sans text-[11px] tracking-[0.4em] uppercase text-aveden-gold mb-10">
              Our Mission
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <h2 className="font-serif font-light text-aveden-white text-3xl sm:text-4xl lg:text-5xl leading-[1.3] tracking-[0.04em] uppercase text-balance">
              To intelligently compound capital and opportunity across
              generations.
            </h2>
          </Reveal>
        </div>
      </section>

      {/* SECTION 3 — FIVE PRINCIPLES */}
      <section
        data-testid="principles-section"
        className="bg-aveden-rich py-24 lg:py-32 border-t border-white/[0.05]"
      >
        <div className="mx-auto max-w-[1600px] px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5">
            {PRINCIPLES.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={p.title} delay={i * 0.08}>
                  <div
                    data-testid={`principle-${p.title.toLowerCase()}`}
                    className="flex flex-col items-center text-center px-6 lg:px-8 py-10 lg:py-6 h-full lg:border-l lg:border-white/[0.08] lg:first:border-l-0"
                  >
                    <Icon
                      size={40}
                      strokeWidth={0.8}
                      className="text-aveden-gold mb-8"
                    />
                    <h3 className="font-serif text-lg text-aveden-white tracking-[0.18em] uppercase mb-5">
                      {p.title}
                    </h3>
                    <p className="font-sans font-light text-sm text-aveden-gray leading-relaxed max-w-[14rem]">
                      {p.text}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 4 — APPROACH (two-column split) */}
      <section
        data-testid="approach-section"
        className="bg-aveden-black grid grid-cols-1 lg:grid-cols-2"
      >
        <div className="relative min-h-[360px] lg:min-h-[640px] overflow-hidden">
          <img
            src={IMAGES.coastline}
            alt="California coastline at golden hour"
            className="absolute inset-0 h-full w-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-aveden-black/20" />
        </div>
        <div className="flex items-center px-6 sm:px-12 lg:px-20 py-24 lg:py-0">
          <Reveal>
            <div className="max-w-xl">
              <p className="font-sans text-[11px] tracking-[0.4em] uppercase text-aveden-gold mb-5">
                Our Approach
              </p>
              <div className="h-px w-12 bg-aveden-gold/60 mb-8" />
              <h2 className="font-serif font-light text-aveden-white text-4xl lg:text-5xl leading-[1.1] tracking-tight uppercase mb-10">
                Focused on what matters.
              </h2>
              <div className="space-y-6 font-sans font-light text-aveden-gray text-base leading-relaxed">
                <p>
                  AVEDEN is a private family office that invests across public
                  and private markets, real assets, and select opportunities
                  where we can create meaningful, long-term value.
                </p>
                <p>
                  We are investors, partners, and stewards.
                  <br />
                  We think in decades, not quarters.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
